import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useState } from 'react';
import UserInfoForm from './components/FamilyInfoForm';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	FamilyRequest,
	UpdatePersonal,
} from '@/api/DTO/family/family.interface';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	LANGUAGE_LIST,
	LANGUAGE_ZH_LIST,
	YESNO,
	ZH_SEX_LIST,
} from '@/common/constants/common';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useGetFamilyDetailQuery,
	useUpdatePersonalMutation,
	useUpdateServiceDetailsMutation,
} from '@/api/family-management/familyApi';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { eightDigitPhone } from '@/utils/validationHelper';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import AppAgeInput from '@/common/components/form-inputs/AppAgeInput';
import {
	ageSchemaFields,
	toAgeFormValues,
	toAgePayload,
} from '@/utils/ageHelper';
import {
	useGetAreaItemListQuery,
	useGetAreaSubItemListMutation,
	useGetDicListQuery,
} from '@/api/common';
import { Language } from '@/api/enum/common.enum';
import { AreaDTO } from '@/api/DTO/common.interface';
export default function FamilyEditPersonalPage() {
	const { t } = useTranslation();
	const { familyId } = useParams();
	const navigate = useNavigate();
	const [updatePersonal] = useUpdatePersonalMutation();
	const [getAreaSubItemList] = useGetAreaSubItemListMutation();
	const [districtList, setDistrictList] = useState<AreaDTO[]>([]);
	const [subDistrictList, setSubDistrictList] = useState<AreaDTO[]>([]);
	const [estateCourtVillageList, setEstateCourtVillageList] = useState<
		AreaDTO[]
	>([]);
	const [buildingBlockList, setBuildingBlockList] = useState<AreaDTO[]>([]);

	const { data: details } = useGetFamilyDetailQuery(familyId || '', {
		refetchOnMountOrArgChange: true,
	});

	const { data: areaItemList } = useGetAreaItemListQuery(
		{ type: 0 },
		{ refetchOnMountOrArgChange: true }
	);

	const defaultValues: DefaultValues<any> = {
		addressLanguage: Language.chinese,
		sex: null,
		...toAgeFormValues(),
	};

	const formSchema = Yup.object().shape({
		nameEn: Yup.string().required(t('errorMessage.pleaseEnter')),
		nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
		sex: Yup.string().required(t('errorMessage.pleaseSelect')),
		hkid: Yup.string().required(t('errorMessage.pleaseEnter')),
		...ageSchemaFields(t, {
			getOriginalEstimatedAge: () => details?.data?.estimatedAge,
		}),
		phone: Yup.string()
			.required(t('errorMessage.pleaseEnter'))
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		homeTel: Yup.string()
			.nullable()
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		addressLanguage: Yup.string().required(t('errorMessage.pleaseSelect')),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchAddressLanguage = methods.watch('addressLanguage');

	useEffect(() => {
		if (details) {
			methods.reset({ ...details.data, ...toAgeFormValues(details.data) });

			if (details.data.region) initAddress(details.data.region, 'region');
			if (details.data.district) initAddress(details.data.district, 'district');
			if (details.data.subdistrict)
				initAddress(details.data.subdistrict, 'subdistrict');
			if (details.data.estateCourtVillage)
				initAddress(details.data.estateCourtVillage, 'estateCourtVillage');
		}
	}, [details]);

	const onSubmit: SubmitHandler<UpdatePersonal> = async (data) => {
		const response = await updatePersonal({ ...data, ...toAgePayload(data) });
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFamilySuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		navigate(`/family-management/family-management-list/detail/${familyId}`);
	};

	const initAddress = async (pid: string, type: string) => {
		if (!pid) return;
		const response = await getAreaSubItemList(pid || '');
		if ('data' in response) {
			if (type == 'region') setDistrictList(response?.data?.data || []);
			if (type == 'district') setSubDistrictList(response?.data?.data || []);
			if (type == 'subdistrict')
				setEstateCourtVillageList(response?.data?.data || []);
			if (type == 'estateCourtVillage')
				setBuildingBlockList(response?.data?.data || []);
		}
	};

	const handleChangeRegion = async (e: any, type: string) => {
		if (!e) return;
		const response = await getAreaSubItemList(e || '');

		if ('data' in response) {
			if (type == 'region') {
				methods.setValue('district', '');
				methods.setValue('subdistrict', '');
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setDistrictList(response?.data?.data || []);
			}
			if (type == 'district') {
				methods.setValue('subdistrict', '');
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setSubDistrictList(response?.data?.data || []);
			}
			if (type == 'subdistrict') {
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setEstateCourtVillageList(response?.data?.data || []);
			}
			if (type == 'estateCourtVillage') {
				methods.setValue('buildingBlock', '');
				setBuildingBlockList(response?.data?.data || []);
			}
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box>
					<Typography variant='h4'>
						{t('drawerListItems.editFamilyMember')}
					</Typography>
					<Card sx={{ marginTop: 3 }}>
						<FormProvider {...methods}>
							<form onSubmit={methods.handleSubmit(onSubmit)}>
								<CardContent sx={{ padding: '24px' }}>
									<Typography
										variant='h6'
										sx={{ fontWeight: 'bold' }}
										marginBottom={2}
									>
										{t('member.personalInformation')}
									</Typography>
									<Grid
										container
										spacing={3}
										marginTop={0}
										marginBottom={3}
										alignItems='center'
									>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='nameEn'
												label={t('preAdmission.name_en') + '*'}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='nameZh'
												label={t('preAdmission.name_zh') + '*'}
											/>
										</Grid>
										<AppAgeInput control={methods.control} />
										<Grid size={6}>
											<Typography variant='body2' color='common.neutral40'>
												{t('preAdmission.sex') + '*'}
											</Typography>
											<AppRadioGroup
												control={methods.control}
												name='sex'
												row
												options={ZH_SEX_LIST}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='hkid'
												label={t('member.HKID') + '*'}
											/>
										</Grid>{' '}
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='phone'
												label={t('member.mobilePhone') + '*'}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='homeTel'
												label={t('member.homeTel')}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='email'
												label={t('member.email')}
											/>
										</Grid>
										<Grid size={12}>
											<Typography variant='body2' color='common.neutral40'>
												{t('preAdmission.addressLanguage') + '*'}
											</Typography>
											<AppRadioGroup
												control={methods.control}
												name='addressLanguage'
												row
												options={LANGUAGE_LIST}
											/>
										</Grid>
										{watchAddressLanguage == Language.english && (
											<>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='region'
														label={t('member.region')}
														options={(areaItemList?.data || []).map(
															(item: { nameEn: any }) => {
																return {
																	...item,
																	name: item.nameEn,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'region');
														}}
													/>
												</Grid>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='district'
														label={t('member.district')}
														options={(districtList || []).map(
															(item: AreaDTO) => {
																return {
																	...item,
																	name: item.nameEn,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'district');
														}}
													/>
												</Grid>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='subdistrict'
														label={t('member.subdistrict')}
														options={(subDistrictList || []).map(
															(item: AreaDTO) => {
																return {
																	...item,
																	name: item.nameEn,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'subdistrict');
														}}
													/>
												</Grid>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='estateCourtVillage'
														label={t('member.estateCourtVillage')}
														options={(estateCourtVillageList || []).map(
															(item: AreaDTO) => {
																return {
																	...item,
																	name: item.nameEn,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'estateCourtVillage');
														}}
													/>
												</Grid>
											</>
										)}
										{watchAddressLanguage == Language.chinese && (
											<>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='region'
														label={t('member.region')}
														options={(areaItemList?.data || []).map(
															(item: { nameZh: any }) => {
																return {
																	...item,
																	name: item.nameZh,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'region');
														}}
													/>
												</Grid>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='district'
														label={t('member.district')}
														options={(districtList || []).map(
															(item: AreaDTO) => {
																return {
																	...item,
																	name: item.nameZh,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'district');
														}}
													/>
												</Grid>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='subdistrict'
														label={t('member.subdistrict')}
														options={(subDistrictList || []).map(
															(item: AreaDTO) => {
																return {
																	...item,
																	name: item.nameZh,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'subdistrict');
														}}
													/>
												</Grid>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='estateCourtVillage'
														label={t('member.estateCourtVillage')}
														options={(estateCourtVillageList || []).map(
															(item: AreaDTO) => {
																return {
																	...item,
																	name: item.nameZh,
																};
															}
														)}
														handleChange={(e: any) => {
															handleChangeRegion(e, 'estateCourtVillage');
														}}
													/>
												</Grid>
											</>
										)}
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='roadStreet'
												label={t('member.roadStreet')}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='roadStreetNo'
												label={t('member.roadStreetNo')}
											/>
										</Grid>
										{watchAddressLanguage == Language.english && (
											<Grid size={6}>
												<AppDropDownMenu
													optional={true}
													control={methods.control}
													name='buildingBlock'
													label={t('member.buildingBlock')}
													options={(buildingBlockList || []).map(
														(item: AreaDTO) => {
															return {
																...item,
																name: item.nameEn,
															};
														}
													)}
												/>
											</Grid>
										)}
										{watchAddressLanguage == Language.chinese && (
											<Grid size={6}>
												<AppDropDownMenu
													optional={true}
													control={methods.control}
													name='buildingBlock'
													label={t('member.buildingBlock')}
													options={(buildingBlockList || []).map(
														(item: AreaDTO) => {
															return {
																...item,
																name: item.nameZh,
															};
														}
													)}
												/>
											</Grid>
										)}
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='buildingBlockSpecify'
												label={t('member.buildingBlockSpecify')}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='floor'
												label={t('member.floor')}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name='flat'
												label={t('member.flat')}
											/>
										</Grid>
									</Grid>
								</CardContent>
								<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
									<Button onClick={handleClose}>{t('button.cancel')}</Button>
									<Button
										type='submit'
										variant='contained'
										endIcon={<CheckCircleOutlineIcon />}
									>
										{t('button.complete')}
									</Button>
								</CardActions>
							</form>
						</FormProvider>
					</Card>
				</Box>
			</Box>
		</>
	);
}
